import { Interpolation, Theme, css } from '@emotion/react';
import dayjs from 'dayjs';
import { getDisplayName } from '@frontend/get-display-name';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InsuranceTypeProvider, ScheduleAppointmentCardInsuranceStatusChip } from '@frontend/insurance-tab';
import { Photos } from '@frontend/photos';
import { ExtendedAppointment } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../constants';
import { useScheduleAppointmentInfoPanelShallowStore } from '../../stores/schedule-appointment-info-panel.store';
import { EventCardStatusChip } from '../schedule-calendar-components/event-card-status-chip';
import { ColorDot } from './color-dot';

interface AppointmentListItemProps {
  as?: React.ElementType;
  containerCss?: Interpolation<Theme>;
  appointment: ExtendedAppointment;
  shouldShowStrikethrough?: boolean;
  isApptTypeArchived?: boolean;
  isProviderArchived?: boolean;
  onPersonClick: () => void;
}

export const AppointmentListItem = ({
  as: Component = 'section',
  containerCss,
  appointment,
  shouldShowStrikethrough,
  onPersonClick,
  isApptTypeArchived = false,
  isProviderArchived = false,
}: AppointmentListItemProps) => {
  const { t } = useTranslation('schedule');
  const { insuranceStatusCalendarDate } = useScheduleAppointmentInfoPanelShallowStore('insuranceStatusCalendarDate');
  const { fullName, legalName } = getDisplayName({
    firstName: appointment.person?.firstName ?? '',
    lastName: appointment.person?.lastName ?? '',
    preferredName: appointment.person?.preferredName ?? '',
  });

  const personFullName = appointment.person ? fullName || t('Unknown') : t('Unknown');
  const startTimeText = appointment.start
    ? `${dayjs(appointment.start).format('h:mm A')} ${
        appointment.duration ?? 0 > 0 ? `(${appointment.duration} ${t('Mins')})` : ''
      }`
    : '';

  return (
    <Component css={[mainContainer, containerCss]}>
      <Photos.ContactProfilePhoto
        size='small'
        personId={appointment.person?.personId ?? ''}
        name={personFullName}
        onClick={onPersonClick}
        title={legalName}
      />
      <div css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5), minWidth: 0 }}>
        <Truncated as={FirstWordBoldText} size='medium'>
          {personFullName}
        </Truncated>
        <div css={{ flex: 1, display: 'flex', gap: theme.spacing(0.5) }}>
          <div css={contentGridContainer}>
            <Truncated as={Text} size='small' color='subdued' css={[shouldShowStrikethrough && lineThroughText]}>
              {startTimeText || '-'}
            </Truncated>
            <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) }}>
              {isProviderArchived && <Icon name='archive-small' color='subdued' />}
              <Truncated as={Text} size='small' color='subdued' css={[shouldShowStrikethrough && lineThroughText]}>
                {appointment.practitionerName || '-'}
              </Truncated>
            </div>
            <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center', minWidth: 0 }}>
              <ColorDot size='small' color={appointment.displayOptions?.primaryColor || PRIMARY_EVENT_COLOR} />
              {isApptTypeArchived && <Icon name='archive-small' color='subdued' />}
              <Truncated as={Text} size='small' color='subdued' css={[shouldShowStrikethrough && lineThroughText]}>
                {appointment.type || '-'}
              </Truncated>
            </div>
            {appointment.operatory && (
              <Truncated as={Text} size='small' color='subdued' css={[shouldShowStrikethrough && lineThroughText]}>
                {appointment.operatory || '-'}
              </Truncated>
            )}
          </div>
          <div css={{ display: 'flex', gap: theme.spacing(0.5) }}>
            <EventCardStatusChip
              appointmentInfoStatus={appointment.statusOfficeView?.toUpperCase()}
              locationId={appointment.locationId || ''}
              eventId={appointment.id || ''}
              appointmentDetails={appointment}
            />
            <InsuranceTypeProvider>
              <ScheduleAppointmentCardInsuranceStatusChip
                locationId={appointment.locationId || ''}
                sourceId={appointment.person?.sourceId || ''}
                calendarDate={insuranceStatusCalendarDate || ''}
                personInfo={{
                  personId: appointment.person?.personId || '',
                  personName: personFullName,
                }}
                pmsID={appointment.person?.pmId || ''}
              />
            </InsuranceTypeProvider>
          </div>
        </div>
      </div>
    </Component>
  );
};

const mainContainer = css({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
});

const contentGridContainer = css({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(0.5),
});

const lineThroughText = css({
  textDecoration: 'line-through',
});
